import { SETTINGS_TAB_LABEL } from "../../lib/pairDevicePath";
// Sugalarm — nav glyphs used by FooterTabs and EdgeRail.
//
// Cycle 42: the `Alerts` entry was retired from the nav surface. The
// alerts engine + predictions still run server-side and the `/alerts`
// route stays mounted for push-notification deep-links — there's just
// no tab into it. The bell glyph and the `"alerts"` NavId went with it.

export type NavId =
  | "now"
  | "history"
  | "log"
  | "household"
  | "settings"
  // Cycle 145: glyph-only id for the appended Clock ACTION item — it is
  // deliberately NOT in NAV_ITEMS (not a routed tab, no active state;
  // tapping it enters alarm-clock mode).
  | "clock"
  // Camera ACTION item — same pattern as "clock": not in NAV_ITEMS (no
  // route, no active state); tapping it hands a fresh meal photo straight
  // to /log/photo via usePhotoCaptureAction.
  | "camera";

interface Props {
  id: NavId;
  size?: number;
  active?: boolean;
}

export function NavIcon({ id, size = 20, active = false }: Props) {
  const c = "currentColor";
  const sw = active ? 2.0 : 1.6;
  const props = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: c,
    strokeWidth: sw,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
  switch (id) {
    case "now":
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="3" fill={active ? c : "none"} />
          <circle cx="12" cy="12" r="8" />
        </svg>
      );
    case "history":
      return (
        <svg {...props}>
          <path d="M3 16 L8 11 L12 14 L17 7 L21 11" />
          <path d="M3 20 H21" opacity="0.4" />
        </svg>
      );
    case "log":
      return (
        <svg {...props}>
          <path d="M5 4 H17 L19 6 V20 H5 Z" />
          <path d="M9 10 H15 M9 14 H13" />
        </svg>
      );
    case "household":
      // Two stylized people; the larger one outlined, the smaller one
      // filled when active so the glyph reads as "group of people".
      return (
        <svg {...props}>
          <circle cx="9" cy="8" r="3" />
          <path d="M3 20 a6 6 0 0 1 12 0" />
          <circle cx="17" cy="10" r="2" fill={active ? c : "none"} />
          <path d="M14 20 a4 4 0 0 1 8 0" opacity="0.7" />
        </svg>
      );
    case "settings":
      // Cycle 78.1: clean 6-tooth cog (Heroicons `cog-6-tooth` outline)
      // replacing the cycle-77 Feather `settings` glyph, whose arc-formed
      // teeth rendered as lumpy blobs at the rail's small size. Conventional
      // trapezoidal teeth read cleanly; same stroke / fill / viewBox
      // conventions as the sibling glyphs, with our own inner hub circle so
      // the `active` fill treatment matches `now` / `household`.
      return (
        <svg {...props}>
          <path d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 0 1 1.37.49l1.296 2.247a1.125 1.125 0 0 1-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 0 1 0 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 0 1-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 0 1-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 0 1-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 0 1-1.369-.49l-1.297-2.247a1.125 1.125 0 0 1 .26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 0 1 0-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 0 1-.26-1.43l1.297-2.247a1.125 1.125 0 0 1 1.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.281Z" />
          <circle cx="12" cy="12" r="3" fill={active ? c : "none"} />
        </svg>
      );
    case "clock":
      // Cycle 145: alarm-clock-mode action glyph — a clock face with a
      // small crescent cut so it reads "bedside clock", matching the
      // sibling stroke conventions.
      return (
        <svg {...props}>
          <circle cx="12" cy="12" r="8" />
          <path d="M12 8 v4 l3 2" />
        </svg>
      );
    case "camera":
      // Camera-body glyph with a filled shutter-lens circle when active,
      // matching the "now" / "settings" active-fill convention.
      return (
        <svg {...props}>
          <path d="M4 8h3l2-3h6l2 3h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z" />
          <circle cx="12" cy="13" r="3.5" fill={active ? c : "none"} />
        </svg>
      );
    default:
      return null;
  }
}

export const NAV_ITEMS: { id: NavId; label: string; path: string }[] = [
  { id: "now", label: "Now", path: "/now" },
  { id: "history", label: "History", path: "/history" },
  { id: "log", label: "Log", path: "/log" },
  { id: "household", label: "Household", path: "/household" },
  { id: "settings", label: SETTINGS_TAB_LABEL, path: "/settings" },
];
